import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts";
import { PageLoader } from "../components";

/**
 * GuestGuard - Protects routes that should only be accessed by unauthenticated users.
 *
 * If the user is already authenticated, they are redirected to the profiles page
 * (or to the URL they were trying to access before login).
 *
 * @example
 * // In router config:
 * {
 *   element: <GuestGuard />,
 *   children: [
 *     { path: "/login", element: <LoginPage /> }
 *   ]
 * }
 */
export function GuestGuard() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return <PageLoader />;
  }

  // Redirect to profiles (or saved location) if already authenticated
  if (isAuthenticated) {
    // Check if there's a saved location to redirect to
    const from = (location.state as { from?: Location })?.from?.pathname || "/profiles";
    return <Navigate to={from} replace />;
  }

  // User is not authenticated, render child routes (login, register, etc.)
  return <Outlet />;
}

export default GuestGuard;
